import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type MudancaChatMessage = {
  id: string;
  mudanca_id: string;
  criado_por: string;
  autor_tipo: 'analista' | 'cliente';
  mensagem: string;
  arquivo_url?: string | null;
  tipo_arquivo?: string | null;
  criado_em: string;
};

export const useMudancaChat = (mudancaId: string | undefined) => {
  const queryClient = useQueryClient();

  // Buscar mensagens do chat
  const { data: chatMessages = [], isLoading, error } = useQuery({
    queryKey: ['mudancaChat', mudancaId],
    queryFn: async () => {
      if (!mudancaId) return [];
      const { data, error } = await supabase
        .from('mudanca_chat_mensagens')
        .select('*')
        .eq('mudanca_id', mudancaId)
        .order('criado_em', { ascending: true });

      if (error) throw error;
      return data as MudancaChatMessage[];
    },
    enabled: !!mudancaId,
  });

  // Enviar nova mensagem
  const sendMessage = useMutation({
    mutationFn: async (payload: Omit<MudancaChatMessage, 'id' | 'criado_em'>) => {
      const { data, error } = await supabase
        .from('mudanca_chat_mensagens')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return data as MudancaChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudancaChat', mudancaId] });
    },
  });

  // Enviar mensagem com arquivo
  const sendMessageWithFile = useMutation({
    mutationFn: async (payload: { 
      mudanca_id: string;
      criado_por: string;
      autor_tipo: 'analista' | 'cliente';
      mensagem: string;
      arquivo_url?: string;
      tipo_arquivo?: string;
    }) => {
      const { data, error } = await supabase
        .from('mudanca_chat_mensagens')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return data as MudancaChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mudancaChat', mudancaId] });
    },
  });

  return { chatMessages, isLoading, error, sendMessage, sendMessageWithFile };
};
